import React from 'react';
import type { ChatDoc, ChatMessage } from '../types/chat';
import type { PerMessageMatches } from '../types/search';
import { buildSearchMatches } from '../utils/chatUtils';

/** Optimistic bubble shown between "send clicked" and "message came back from the server". */
export interface PendingChatMessage {
    text: string;
    files: File[];
    // Carried so a retry re-sends the document, not just its flattened HTML.
    doc?: ChatDoc;
    failed?: boolean;
}

export interface UseChatSurfaceOptions {
    /** Message feed the surface renders — drives scrolling and search. */
    messages: ChatMessage[];
    /**
     * Whether the surface is currently visible. Surfaces with an open/closed
     * lifecycle pass their open flag so scrolling stays inert while closed;
     * always-mounted surfaces leave it alone.
     */
    isActive?: boolean;
    /** Scroll to the bottom whenever the surface becomes active. */
    scrollToBottomOnActivate?: boolean;
    /** Defer the scroll-on-new-message to a macrotask instead of running it inline. */
    deferNewMessageScroll?: boolean;
}

export interface UseChatSurfaceResult {
    // Refs
    containerRef: React.MutableRefObject<HTMLDivElement | null>;
    listRef: React.MutableRefObject<HTMLDivElement | null>;
    bottomRef: React.MutableRefObject<HTMLDivElement | null>;
    searchInputRef: React.MutableRefObject<HTMLInputElement | null>;

    // Scrolling
    scrollToBottom: (behavior?: ScrollBehavior) => void;
    scrollToMatchIdx: (idx: number, behavior?: ScrollBehavior) => void;

    // Send / pending lifecycle
    sending: boolean;
    setSending: React.Dispatch<React.SetStateAction<boolean>>;
    pendingMessage: PendingChatMessage | null;
    beginPendingMessage: (text: string, files: File[], doc?: ChatDoc) => void;
    markPendingMessageFailed: () => void;
    dismissPendingMessage: () => void;

    // Attachment staging
    pendingFiles: File[];
    setPendingFiles: React.Dispatch<React.SetStateAction<File[]>>;
    onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onAddFiles: (files: File[]) => void;
    onRemoveFile: (e: React.MouseEvent<HTMLButtonElement>) => void;
    failedFileNames: string[];
    setFailedFileNames: React.Dispatch<React.SetStateAction<string[]>>;
    clearFailedFileNames: () => void;

    // Search
    searchQuery: string;
    setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
    isSearchOpen: boolean;
    activeMatchIdx: number;
    perMessageMatches: PerMessageMatches;
    flatMatchesCount: number;
    onPrevMatch: () => void;
    onNextMatch: () => void;
    closeSearch: () => void;
    toggleSearch: () => void;
}

/**
 * Container-level machinery shared by every chat surface: scroll management,
 * the optimistic pending-message lifecycle, attachment staging and search.
 * Everything app-specific (providers, submit/edit semantics, chrome) stays in
 * the surface that calls this.
 */
export const useChatSurface = ({
    messages,
    isActive = true,
    scrollToBottomOnActivate = false,
    deferNewMessageScroll = true
}: UseChatSurfaceOptions): UseChatSurfaceResult => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const listRef = React.useRef<HTMLDivElement | null>(null);
    const bottomRef = React.useRef<HTMLDivElement | null>(null);
    const searchInputRef = React.useRef<HTMLInputElement | null>(null);

    const [sending, setSending] = React.useState(false);
    const [pendingMessage, setPendingMessage] = React.useState<PendingChatMessage | null>(null);
    const [pendingFiles, setPendingFiles] = React.useState<File[]>([]);
    const [failedFileNames, setFailedFileNames] = React.useState<string[]>([]);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [activeMatchIdx, setActiveMatchIdx] = React.useState(-1);
    const [isSearchOpen, setIsSearchOpen] = React.useState(false);

    const previousMessageCountRef = React.useRef(0);
    const pendingMessageSentRef = React.useRef(false);

    // --- Scroll management ---
    const scrollToBottom = React.useCallback((behavior: ScrollBehavior = 'auto') => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior, block: 'end' });
        else if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, []);

    const scrollToMatchIdx = React.useCallback(
        (idx: number, behavior: ScrollBehavior = 'smooth') => {
            if (idx < 0) return;
            const el = listRef.current?.querySelector(`[data-match-idx="${idx}"]`);
            if (el) el.scrollIntoView({ behavior, block: 'center' });
        },
        []
    );

    React.useEffect(() => {
        if (scrollToBottomOnActivate && isActive) setTimeout(() => scrollToBottom('auto'), 0);
    }, [isActive, scrollToBottomOnActivate, scrollToBottom]);

    React.useEffect(() => {
        if (!isActive) return;
        if (deferNewMessageScroll) setTimeout(() => scrollToBottom('auto'), 0);
        else scrollToBottom('auto');
    }, [messages.length, isActive, deferNewMessageScroll, scrollToBottom]);

    React.useEffect(() => {
        if (pendingMessage && isActive) setTimeout(() => scrollToBottom('smooth'), 0);
    }, [pendingMessage, isActive, scrollToBottom]);

    // --- Pending message lifecycle ---
    React.useEffect(() => {
        if (
            pendingMessage &&
            pendingMessageSentRef.current &&
            messages.length > previousMessageCountRef.current
        ) {
            setPendingMessage(null);
            pendingMessageSentRef.current = false;
            setTimeout(() => scrollToBottom('smooth'), 0);
        }
    }, [messages.length, pendingMessage, scrollToBottom]);

    React.useEffect(() => {
        if (pendingMessage && pendingMessageSentRef.current) {
            const timeoutMs = pendingMessage.files.length > 0 ? 60_000 : 10_000;
            const timeout = setTimeout(() => {
                if (pendingMessageSentRef.current) {
                    setPendingMessage(null);
                    pendingMessageSentRef.current = false;
                }
            }, timeoutMs);
            return () => clearTimeout(timeout);
        }
    }, [pendingMessage]);

    const messagesLength = messages.length;
    const beginPendingMessage = React.useCallback(
        (text: string, files: File[], doc?: ChatDoc) => {
            previousMessageCountRef.current = messagesLength;
            pendingMessageSentRef.current = true;
            setPendingMessage({ text, files, doc });
        },
        [messagesLength]
    );

    const markPendingMessageFailed = React.useCallback(() => {
        pendingMessageSentRef.current = false;
        setPendingMessage((prev) => (prev ? { ...prev, failed: true } : null));
    }, []);

    const dismissPendingMessage = React.useCallback(() => setPendingMessage(null), []);

    // --- Attachment staging ---
    const onFileChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        setPendingFiles((prev) => [...prev, ...files]);
        e.target.value = '';
    }, []);

    const onAddFiles = React.useCallback((files: File[]) => {
        setPendingFiles((prev) => [...prev, ...files]);
    }, []);

    const onRemoveFile = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        const idxAttr = (e.currentTarget as HTMLButtonElement).dataset.index;
        if (idxAttr == null) return;
        const removeIndex = Number(idxAttr);
        if (Number.isNaN(removeIndex)) return;
        setPendingFiles((prev) => prev.filter((_, j) => j !== removeIndex));
    }, []);

    const clearFailedFileNames = React.useCallback(() => setFailedFileNames([]), []);

    // --- Search ---
    const { perMessageMatches, flatMatchesCount } = React.useMemo(
        () => buildSearchMatches(messages, searchQuery),
        [messages, searchQuery]
    );

    React.useEffect(() => {
        if (!searchQuery.trim()) {
            setActiveMatchIdx(-1);
            return;
        }
        setActiveMatchIdx(flatMatchesCount > 0 ? 0 : -1);
    }, [searchQuery, flatMatchesCount]);

    React.useEffect(() => {
        if (activeMatchIdx >= 0) scrollToMatchIdx(activeMatchIdx);
    }, [activeMatchIdx, scrollToMatchIdx]);

    React.useEffect(() => {
        if (isSearchOpen && searchInputRef.current) searchInputRef.current.focus();
    }, [isSearchOpen]);

    // Cmd+F / Ctrl+F opens chat search (only when focus is in chat)
    React.useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (
                (e.metaKey || e.ctrlKey) &&
                e.key === 'f' &&
                containerRef.current?.contains(document.activeElement)
            ) {
                e.preventDefault();
                setIsSearchOpen(true);
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    const onPrevMatch = React.useCallback(() => {
        if (flatMatchesCount === 0) return;
        setActiveMatchIdx(
            (prev) => ((prev < 0 ? 0 : prev) - 1 + flatMatchesCount) % flatMatchesCount
        );
    }, [flatMatchesCount]);

    const onNextMatch = React.useCallback(() => {
        if (flatMatchesCount === 0) return;
        setActiveMatchIdx((prev) => ((prev < 0 ? -1 : prev) + 1) % flatMatchesCount);
    }, [flatMatchesCount]);

    const closeSearch = React.useCallback(() => {
        setIsSearchOpen(false);
        setSearchQuery('');
        setActiveMatchIdx(-1);
    }, []);

    const toggleSearch = React.useCallback(() => setIsSearchOpen((v) => !v), []);

    return {
        containerRef,
        listRef,
        bottomRef,
        searchInputRef,
        scrollToBottom,
        scrollToMatchIdx,
        sending,
        setSending,
        pendingMessage,
        beginPendingMessage,
        markPendingMessageFailed,
        dismissPendingMessage,
        pendingFiles,
        setPendingFiles,
        onFileChange,
        onAddFiles,
        onRemoveFile,
        failedFileNames,
        setFailedFileNames,
        clearFailedFileNames,
        searchQuery,
        setSearchQuery,
        isSearchOpen,
        activeMatchIdx,
        perMessageMatches,
        flatMatchesCount,
        onPrevMatch,
        onNextMatch,
        closeSearch,
        toggleSearch
    };
};
